import React, {useEffect, useState} from 'react';
import classNames from 'classnames';
import {useDispatch} from 'react-redux';
import {InfoCard} from "@eika/card";
import { get } from '@eika-infrastruktur/request';
import {DefaultConfig} from '../../default-config';
import Aktivering from '../../components/Aktivering/Aktivering';
import LoggInnPaAvtale from '../../components/LoggInnPaAvtale/LoggInnPaAvtale';
import {generateId, scrollBodyTop} from '../../common/util/Functions';
import {useActivation, useTranslation} from '../../common/util/Hook';
import { StringObject, Translations } from '../../types';
import {ActivateOrderParam, ActivateOrderResult, StartActivationParam, StartActivationResult,} from '../../generated-types';
import Constant from '../../static/data/Constant';

import {getActivation, postActivateOrder, setActivateOrder, setActivation, setSendPassword} from '../../actions/activation';

import './Avtale.scss';
import '@eika/layout-style'
import Spinner from "../../common/Spinner/Spinner";
import { SERVICE_PATH } from '../../actions';
import Message from '../../common/Message/Message';
import { LanguageSelector } from '../../components/LanguageSelector';

export interface AvtaleProps {
    config: typeof DefaultConfig;
}

interface StepItem {
    id: string;
    title?: string;
    description?: string | string[];
    error?: boolean;
    element?: React.ReactElement;
}

interface State {
    id: string;
    error: string;
    message: string;
    loading: boolean;
    dispatch: (s: any) => void;
    stepView: {
        selected: number;
        stepList: StepItem[];
    };
}

const GENERIC_ERROR_KEY = 'opsNoeGikkGalt';

const translate = (translated: Translations, key: string): string =>
    (translated[key as keyof Translations] as string) || key;

const login = (state: State, setState: (s: State) => void, data: StringObject): void => {
    state.dispatch(setActivation(undefined));
    setState({...state, loading: true, error: ''});

    const param: StartActivationParam = {
        customerId: data.customerId || '',
        otp: data.otp || '',
    };
    state.dispatch(getActivation(param));
};

const activate = (state: State, setState: (s: State) => void, data: StringObject, activation?: StartActivationResult): void => {
    state.dispatch(setActivateOrder(undefined));
    setState({...state, loading: true, error: ''});

    const param: ActivateOrderParam = {
        password: data.password || '',
        reference: activation?.reference || '',
    };

    state.dispatch(postActivateOrder(param));
};

const startOver = (state: State, setState: (s: State) => void): void => {
    setState({...state, error: '', stepView: {...state.stepView, selected: 0}})

    state.dispatch(setActivation(undefined));
    state.dispatch(setActivateOrder(undefined));
    state.dispatch(setSendPassword(undefined));
};

const updateState = (state: State, setState: (s: State) => void, activation?: StartActivationResult, activated?: ActivateOrderResult): void => {
    const base = {loading: false, error: '', message: ''};

    if (activation === null && !state.error) {
        setState({...state, ...base, error: GENERIC_ERROR_KEY});
    } else if (activation?.success === true) {
        if (activated === null && !state.error) {
            setState({...state, ...base, error: GENERIC_ERROR_KEY});
        } else if (activated?.success === true && state.stepView.selected !== 2) {
            scrollBodyTop(undefined, undefined, state.id, -40);
            setState({...state, ...base, stepView: {...state.stepView, selected: 2}});
        } else if (activated?.success === false) {
            const key = activated?.result || GENERIC_ERROR_KEY;
            if (!state.error) {
                setState({...state, ...base, error: key});
            }
        } else if (state.stepView.selected === 0) {
            scrollBodyTop(undefined, undefined, state.id, -40);
            setState({...state, ...base, stepView: {...state.stepView, selected: 1}});
        }
    } else if (activation?.success === false) {
        const key = activation?.result || GENERIC_ERROR_KEY;
        if (activation?.result === Constant.avtaleOTPnoOrder) {
            if (!state.message) {
                setState({...state, ...base, message: key});
            }
        } else if (!state.error) {
            setState({...state, ...base, error: key});
        }
    }
};

const Avtale: React.FC<AvtaleProps> = (props): React.ReactElement => {
    const dispatch = useDispatch();
    const {activation, activated} = useActivation();
    const translated = useTranslation();
    const [state, setState] = useState<State>({
        id: generateId('avtale'),
        error: '',
        message: '',
        loading: false,
        dispatch,
        stepView: {
            selected: 0,
            stepList: [{
                id: 'bank-id-avtale-start',
            }, {
                id: 'bank-id-avtale-aktivering',
            }, {
                id: 'bank-id-avtale-slutt',
                title: translated.bankIdAvtaleSluttTittel,
                description: [
                    translated.bankIdAvtaleSluttBeskrivelse1,
                    translated.bankIdAvtaleSluttBeskrivelse2,
                ]
            }]
        },
    });

    useEffect(() => {
        setState(prevState => {
            return {
                ...prevState,
                stepView: {
                    ...prevState.stepView,
                    stepList: [
                        prevState.stepView.stepList[0],
                        prevState.stepView.stepList[1],
                        {...prevState.stepView.stepList[2],
                            title: translated.bankIdAvtaleSluttTittel,
                            description: [
                                translated.bankIdAvtaleSluttBeskrivelse1,
                                translated.bankIdAvtaleSluttBeskrivelse2,
                            ]
                        },
                    ]
                }
            }
        })
    }, [translated]);

    const [isDown, setIsDown] = useState(false)

    useEffect(() => {
        const isDownForMaintenance = async (): Promise<boolean> => get(`${SERVICE_PATH}/overview/downformaintenance`)
        isDownForMaintenance().then( (down: boolean) => setIsDown(down));
        updateState(state, setState, activation, activated);

    }, [state, setState, activation, activated]);

    if(isDown) {
        return (
            <div className={classNames('avtale-wrapper')}>
                <h1 className="paragraph">{translated.bankIdAvtale}</h1>
                <Message type="error-page" text={translated.bankIdInfoNede} />
            </div>
        )
    }
    return (
        <InfoCard id={state.id} className={classNames('avtale-wrapper', `-step-${state.stepView.selected}`, {
            '-no-bank-id-ordered': !!state.message,
        })}>
            <h1 className="avtale-title paragraph">{translated.bankIdAvtale}</h1>
            {state.loading && <Spinner namespace="avtale-loader"/>}
            {!!state.message && <p aria-live="polite" className="avtale-message">{translate(translated, state.message)}</p>}

            <div className="step-panel-wrapper">
                {state.stepView.stepList.map((data: StepItem, i: number) => {
                    return i === state.stepView.selected ?
                        <div id={`${data.id}-panel`} key={`${data.id}-panel`} className="step-panel">
                            {!!data.title && <h2 className="paragraph-small">{data.title}</h2>}

                            {(data.description instanceof Array ? data.description : [data.description]).map((text?: string) => {
                                return text ? <p key={`description-${i}-${text || 'none'}`} className="paragraph-small">{text}</p> : null
                            })}

                            {data.id === 'bank-id-avtale-start' &&
                                <LoggInnPaAvtale {...props} submit={(data: StringObject) => {
                                    login(state, setState, data);
                                }} failed={translate(translated, state.error)}/>
                            }

                            {data.id === 'bank-id-avtale-aktivering' &&
                                <Aktivering {...props} activation={activation} useH2 submit={(data: StringObject) => {
                                    activate(state, setState, data, activation);
                                }} failed={translate(translated, state.error)} startOver={() => startOver(state, setState)}/>
                            }
                            {data.element}
                        </div>
                        : null
                })}
                <LanguageSelector />
            </div>
        </InfoCard>
    );
};

export default Avtale;
